import React, { useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';
import { Draggable, Droppable } from 'react-beautiful-dnd';

import DeleteRecruitmentStatusModal from './DeleteRecruitmentStatusModal';
import RecruitmentCategoryPill from './RecruitmentCategoryPill';
import { RECRUITMENT_SUMMARY_STALE_EVENT, notifyRecruitmentStatusesChanged } from './recruitmentStatusCategories';
import RecruitmentStatusLabel from './RecruitmentStatusLabel';
import RecruitmentStatusModal from './RecruitmentStatusModal';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import { DragAndDrop } from 'CommonComponents/DragAndDrop';
import TextButton from 'CommonComponents/TextButton';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * The event's recruitment statuses. Their order here is the order the position
 * form's dropdown offers them in.
 */
export default function WorkforceAssumptionRecruitmentStatuses({ urls, canManage }) {
    const [statuses, setStatuses] = useState([]);
    const [editedStatus, setEditedStatus] = useState(null);
    const [deletedStatus, setDeletedStatus] = useState(null);
    const [saveModal, setSaveModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    const saveModalRef = useRef();
    const deleteModalRef = useRef();

    const toast = useToast();

    useEffect(() => {
        setSaveModal(new Modal(saveModalRef.current, modalOptions(false)));
        setDeleteModal(new Modal(deleteModalRef.current, modalOptions(false)));
        fetchStatuses();
    }, []);

    const fetchStatuses = () =>
        apiClient
            .get(urls.index)
            .then(({ data }) => {
                setStatuses(data.data);
                return data.data;
            })
            .catch(() => {
                toast.error($t('Recruitment statuses could not be loaded'));
                return null;
            });

    // A bulk update on the Positions tab of the same page changes "Positions using".
    useEffect(() => {
        window.addEventListener(RECRUITMENT_SUMMARY_STALE_EVENT, fetchStatuses);

        return () => window.removeEventListener(RECRUITMENT_SUMMARY_STALE_EVENT, fetchStatuses);
    }, [urls.index]);

    // The delete dialog found the status in use although this list said it was not:
    // reload, and hand the dialog the status with its current count.
    const refreshDeletedStatus = () =>
        fetchStatuses().then((list) => {
            const fresh = list?.find((item) => item.id === deletedStatus?.id);
            if (fresh) {
                setDeletedStatus(fresh);
            }
        });

    const openSaveModal = (status = null) => {
        setEditedStatus(status);
        saveModal?.show();
    };

    const openDeleteModal = (status) => {
        setDeletedStatus(status);
        deleteModal?.show();
    };

    const closeModals = () => {
        saveModal?.hide();
        deleteModal?.hide();
        setEditedStatus(null);
        setDeletedStatus(null);
    };

    const handleSaved = () => {
        closeModals();
        fetchStatuses();
        notifyRecruitmentStatusesChanged();
    };

    const toggleActive = (status) => {
        apiClient
            .put(urls.update.replace('RECRUITMENT_STATUS_ID', status.id), {
                name: status.name,
                category: status.category,
                colour: status.colour,
                is_active: !status.is_active,
            })
            .then(({ data }) => {
                setStatuses((prev) => prev.map((item) => (item.id === status.id ? data.data : item)));
                notifyRecruitmentStatusesChanged();
            })
            .catch((err) => toast.error(err.response?.data?.message || $t('Something went wrong while saving the recruitment status')));
    };

    const restoreDefaults = () => {
        apiClient
            .post(urls.restoreDefaults)
            .then(({ data }) => {
                setStatuses(data.data);
                notifyRecruitmentStatusesChanged();
                toast.success($t('Default recruitment statuses restored'));
            })
            .catch((err) => toast.error(err.response?.data?.message || $t('Something went wrong while restoring the default statuses')));
    };

    const handleDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        const previous = statuses;
        const reordered = [...statuses];
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setStatuses(reordered);

        apiClient
            .put(urls.order, { ids: reordered.map((status) => status.id) })
            .then(({ data }) => {
                setStatuses(data.data);
                notifyRecruitmentStatusesChanged();
            })
            .catch((err) => {
                setStatuses(previous);
                toast.error(err.response?.data?.message || $t('Something went wrong while reordering the statuses'));
            });
    };

    return (
        <div className="tw-space-y-2 tw-my-3">
            <div className="tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary">
                <div className="tw-flex tw-justify-between tw-items-start tw-mb-5">
                    <div>
                        <h3 className="table__header">{$t('Recruitment Statuses')}</h3>
                        <p className="tw-mt-1 tw-max-w-[760px] tw-text-xs tw-leading-snug tw-text-charcoal-75">
                            {$t(
                                'Optional statuses you can set on each position to track recruitment progress. Each status maps to one of four fixed categories (not started, in market, offer stage, filled) so the workforce dashboard and the monthly management pack can summarise progress consistently. Drag to reorder the dropdown.',
                            )}
                        </p>
                    </div>
                    {canManage && (
                        <div className="tw-flex tw-gap-x-1.5">
                            <Button variant="outline" onClick={restoreDefaults}>
                                {$t('Restore defaults')}
                            </Button>
                            <Button variant="primary" onClick={() => openSaveModal()}>
                                {$t('Add Status')}
                            </Button>
                        </div>
                    )}
                </div>

                <div className="inbud-table_wrapper">
                    <table className="inbud-table tw-table-fixed">
                        <thead>
                            <tr>
                                {canManage && <th className="tw-w-10" aria-label={$t('Reorder')} />}
                                <th className="tw-w-64">{$t('Status')}</th>
                                <th className="tw-w-40">{$t('Category')}</th>
                                <th className="tw-w-36">{$t('Positions using')}</th>
                                <th className="tw-w-28">{$t('Active')}</th>
                                {canManage && <th className="tw-w-24">{$t('Actions')}</th>}
                            </tr>
                        </thead>
                        <DragAndDrop onDragEnd={handleDragEnd}>
                            <Droppable droppableId="recruitment-statuses" isDropDisabled={!canManage}>
                                {(droppable) => (
                                    <tbody ref={droppable.innerRef} {...droppable.droppableProps}>
                                        {statuses.map((status, index) => (
                                            <Draggable
                                                key={status.id}
                                                draggableId={`recruitment-status-${status.id}`}
                                                index={index}
                                                isDragDisabled={!canManage}
                                            >
                                                {(draggable) => (
                                                    <tr ref={draggable.innerRef} {...draggable.draggableProps} className="tw-bg-white">
                                                        {canManage && (
                                                            <td {...draggable.dragHandleProps} title={$t('Drag to reorder')}>
                                                                <i className="fas fa-grip-vertical text-light-grey" />
                                                            </td>
                                                        )}
                                                        <td>
                                                            <RecruitmentStatusLabel status={status} />
                                                        </td>
                                                        <td>
                                                            <RecruitmentCategoryPill category={status.category} label={status.category_label} />
                                                        </td>
                                                        <td className="inbud-numeric-cell">{status.positions_using}</td>
                                                        <td>
                                                            <ToggleInput
                                                                checked={status.is_active}
                                                                disabled={!canManage}
                                                                noBottomSpacing={true}
                                                                aria-label={$t('Active')}
                                                                onChange={() => toggleActive(status)}
                                                            />
                                                        </td>
                                                        {canManage && (
                                                            <td>
                                                                <div className="tw-flex tw-items-center tw-gap-x-3">
                                                                    <TextButton variant="primary" onClick={() => openSaveModal(status)} title={$t('Edit')}>
                                                                        <EditIcon className="tw-w-3 tw-h-3" />
                                                                    </TextButton>
                                                                    <TextButton variant="danger" onClick={() => openDeleteModal(status)} title={$t('Delete')}>
                                                                        <TrashIcon className="tw-w-3 tw-h-3" />
                                                                    </TextButton>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {droppable.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </DragAndDrop>
                    </table>
                </div>
                <p className="tw-mt-2.5 tw-mb-0 tw-max-w-[760px] tw-text-xs tw-leading-snug tw-text-charcoal-75">
                    {$t(
                        'Positions without a status show as "Not set" everywhere and are counted as their own category in the dashboard and the management pack, so totals always reconcile. Inactive statuses stay on positions that already use them but are hidden from the dropdown. Categories are fixed and cannot be edited.',
                    )}
                </p>
            </div>

            <RecruitmentStatusModal
                modalId="save-recruitment-status-modal"
                modalRef={saveModalRef}
                status={editedStatus}
                storeUrl={urls.store}
                updateUrl={urls.update}
                onClose={closeModals}
                onSuccess={handleSaved}
            />

            <DeleteRecruitmentStatusModal
                modalId="delete-recruitment-status-modal"
                modalRef={deleteModalRef}
                status={deletedStatus}
                statuses={statuses}
                deleteUrl={urls.destroy}
                onClose={closeModals}
                onSuccess={handleSaved}
                onFoundInUse={refreshDeletedStatus}
            />
        </div>
    );
}
